import { apiClient } from './client';

/**
 * Get AI job status for a course.
 * @param {string} courseId
 * @returns {{ status: 'idle'|'running'|'completed'|'failed', startedAt: string|null, completedAt: string|null, errorMessage: string|null }}
 */
export async function getCourseAiStatus(courseId) {
  const { data } = await apiClient.get(`/courses/${courseId}/ai-status`);
  return data;
}

/**
 * Get AI inbox count — courses with at least one module awaiting skill/prereq review.
 * @returns {{ count: number }}
 */
export async function getAiInboxCount() {
  const { data } = await apiClient.get('/courses/ai-inbox-count');
  return data;
}

/**
 * Add a skill to a module.
 * @param {string} courseId
 * @param {string} moduleId
 * @param {{ skillSlug: string, name?: string, translations?: { ru?: string, uz?: string } }} body
 */
export async function addModuleSkill(courseId, moduleId, body) {
  const { data } = await apiClient.post(`/courses/${courseId}/modules/${moduleId}/skills`, body);
  return data;
}

/**
 * Remove a skill from a module.
 * @param {string} courseId
 * @param {string} moduleId
 * @param {string} skillSlug
 */
export async function removeModuleSkill(courseId, moduleId, skillSlug) {
  const { data } = await apiClient.delete(`/courses/${courseId}/modules/${moduleId}/skills/${skillSlug}`);
  return data;
}

/**
 * Mark a single module's skills as reviewed.
 * @param {string} courseId
 * @param {string} moduleId
 */
export async function markModuleSkillsReviewed(courseId, moduleId) {
  const { data } = await apiClient.patch(`/courses/${courseId}/modules/${moduleId}/skills/review`);
  return data;
}

/**
 * Mark all modules' skills reviewed in one request.
 * @param {string} courseId
 */
export async function reviewAllSkills(courseId) {
  const { data } = await apiClient.post(`/courses/${courseId}/skills/review-all`);
  return data;
}

/**
 * Add a prerequisite edge.
 * @param {string} courseId
 * @param {string} moduleId - the module that gains a requirement
 * @param {string} requiresModuleId - the module being required
 */
export async function addPrerequisite(courseId, moduleId, requiresModuleId) {
  const { data } = await apiClient.post(`/courses/${courseId}/modules/${moduleId}/prerequisites`, { requiresModuleId });
  return data;
}

/**
 * Remove a prerequisite edge.
 * @param {string} courseId
 * @param {string} moduleId
 * @param {string} requiresModuleId
 */
export async function removePrerequisite(courseId, moduleId, requiresModuleId) {
  const { data } = await apiClient.delete(`/courses/${courseId}/modules/${moduleId}/prerequisites/${requiresModuleId}`);
  return data;
}

/**
 * Mark a single module's prerequisites as reviewed.
 * @param {string} courseId
 * @param {string} moduleId
 */
export async function markModulePrerequisitesReviewed(courseId, moduleId) {
  const { data } = await apiClient.patch(`/courses/${courseId}/modules/${moduleId}/prerequisites/review`);
  return data;
}

/**
 * Get tenant skill taxonomy (for add-skill autocomplete).
 * @returns {Array<{ slug: string, name: string }>}
 */
export async function getSkillTaxonomy() {
  const { data } = await apiClient.get('/skills');
  return data;
}

/**
 * Trigger a manual re-analysis AI job for a course.
 * Returns the full axios response so the caller can inspect headers (e.g. Retry-After).
 *
 * @param {string} courseId
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function triggerReanalysis(courseId) {
  return apiClient.post(`/courses/${courseId}/skills/reanalyze`);
}
